import React, { useState } from 'react';
import { ActionIcon, Box, Group, Image, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCar, IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronUp, IconFileAnalytics, IconHome2, IconSettings } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import WorkspaceSwitcher from './WorkspaceSwitcher.jsx';
import SidebarProfileMenu from './SidebarProfileMenu.jsx';
import TopbarSearch from './TopbarSearch.jsx';

const Sidebar = ({ onProModeChange, collapsed, onToggleCollapsed, isMobile = false, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const effectiveCollapsed = !isMobile && collapsed;
  const handleNavigate = (targetPath) => {
    navigate(targetPath);
    if (onNavigate) {
      onNavigate();
    }
  };
  const dashboardLike = path === '/' || path.startsWith('/dashboard') || path.startsWith('/group/') || path.startsWith('/vehicle/');
  const driveOffsActive = path.startsWith('/manual-entry') || path.startsWith('/drive-off-report');
  const [driveOffsOpen, setDriveOffsOpen] = useState(driveOffsActive);
  const navItems = [
    { label: 'Live', icon: IconHome2, active: dashboardLike, onClick: () => handleNavigate('/dashboard') },
  ];
  const reportsItem = { label: 'Analytics', icon: IconFileAnalytics, active: path.startsWith('/reportDashboard'), onClick: () => handleNavigate('/reportDashboard') };
  const settingsItem = { label: 'Settings', icon: IconSettings, active: path.startsWith('/settings'), onClick: () => handleNavigate('/settings') };

  return (
    <Stack h="100%" justify="space-between" gap="md" p={effectiveCollapsed ? 8 : 12} style={{ backgroundColor: '#0B0F14' }}>
      <Stack gap={effectiveCollapsed ? 'sm' : 'md'} align={effectiveCollapsed ? 'center' : 'stretch'}>
        {effectiveCollapsed ? (
          <>
            <Box>
              <Image
                src="/images/logo-white-mark.svg"
                alt="DriveOffShield"
                w={30}
                fit="contain"
              />
            </Box>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onToggleCollapsed}
              aria-label="Expand sidebar"
              size="lg"
            >
              <IconChevronRight size={16} />
            </ActionIcon>
          </>
        ) : (
          <Group justify="space-between" align="center" wrap="nowrap">
            <Box>
              <Image
                src="/images/logo-white.svg"
                alt="DriveOffShield"
                w={150}
                fit="contain"
              />
            </Box>
            {!isMobile && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={onToggleCollapsed}
                aria-label="Collapse sidebar"
                size="lg"
              >
                <IconChevronLeft size={16} />
              </ActionIcon>
            )}
          </Group>
        )}

        {!effectiveCollapsed && (
          <>
            <TopbarSearch onProModeChange={onProModeChange} dark />
            <WorkspaceSwitcher />
            <Text
              size="10px"
              c="#6B7280"
              fw={700}
              tt="uppercase"
              w="100%"
              px={12}
              ta="left"
              style={{ letterSpacing: '0.08em' }}
            >
              Main Menu
            </Text>
          </>
        )}

        <Stack gap={effectiveCollapsed ? 10 : 6} align={effectiveCollapsed ? 'center' : 'stretch'}>
          {effectiveCollapsed ? (
            <>
              {navItems.map(({ label, icon: Icon, active, onClick }) => (
                <Tooltip key={label} label={label} position="right">
                  <UnstyledButton
                    onClick={onClick}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: active ? '1px solid rgba(99, 102, 241, 0.45)' : '1px solid transparent',
                      backgroundColor: active ? '#4F46E5' : 'transparent',
                      color: active ? '#FFFFFF' : '#94A3B8',
                    }}
                  >
                    <Icon size={18} />
                  </UnstyledButton>
                </Tooltip>
              ))}
              <Tooltip label="Drive Offs" position="right">
                <UnstyledButton
                  onClick={() => setDriveOffsOpen((o) => !o)}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: driveOffsActive ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                    backgroundColor: driveOffsActive ? '#4F46E5' : 'transparent',
                    color: driveOffsActive ? '#FFFFFF' : '#94A3B8',
                  }}
                >
                  <IconCar size={18} />
                </UnstyledButton>
              </Tooltip>
              <Tooltip label={reportsItem.label} position="right">
                <UnstyledButton
                  onClick={reportsItem.onClick}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: reportsItem.active ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                    backgroundColor: reportsItem.active ? '#4F46E5' : 'transparent',
                    color: reportsItem.active ? '#FFFFFF' : '#94A3B8',
                  }}
                >
                  <reportsItem.icon size={18} />
                </UnstyledButton>
              </Tooltip>
              <Tooltip label={settingsItem.label} position="right">
                <UnstyledButton
                  onClick={settingsItem.onClick}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: settingsItem.active ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                    backgroundColor: settingsItem.active ? '#4F46E5' : 'transparent',
                    color: settingsItem.active ? '#FFFFFF' : '#94A3B8',
                  }}
                >
                  <settingsItem.icon size={18} />
                </UnstyledButton>
              </Tooltip>
            </>
          ) : (
            <>
              {navItems.map(({ label, icon: Icon, active, onClick }) => (
                <UnstyledButton
                  key={label}
                  onClick={onClick}
                  style={{
                    width: '100%',
                    minHeight: 40,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '9px 12px',
                    backgroundColor: active ? '#4F46E5' : 'transparent',
                    color: active ? '#FFFFFF' : '#E5E7EB',
                    border: active ? '1px solid rgba(99, 102, 241, 0.45)' : '1px solid transparent',
                    boxShadow: active ? '0 0 0 1px rgba(99,102,241,0.22)' : 'none',
                  }}
                >
                  <Icon size={17} color={active ? '#FFFFFF' : '#9CA3AF'} />
                  <Text size="14px" fw={500} c={active ? '#FFFFFF' : '#E5E7EB'}>
                    {label}
                  </Text>
                </UnstyledButton>
              ))}

              {/* Drive Offs accordion */}
              <UnstyledButton
                onClick={() => setDriveOffsOpen((o) => !o)}
                style={{
                  width: '100%',
                  minHeight: 40,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '9px 12px',
                  backgroundColor: driveOffsActive ? '#4F46E5' : 'transparent',
                  color: driveOffsActive ? '#FFFFFF' : '#E5E7EB',
                  border: driveOffsActive ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                  boxShadow: driveOffsActive ? '0 0 0 1px rgba(99,102,241,0.22)' : 'none',
                }}
              >
                <Group gap={12} align="center" wrap="nowrap">
                  <IconCar size={17} color={driveOffsActive ? '#FFFFFF' : '#9CA3AF'} />
                  <Text size="14px" fw={500} c={driveOffsActive ? '#FFFFFF' : '#E5E7EB'}>
                    Drive Offs
                  </Text>
                </Group>
                {driveOffsOpen
                  ? <IconChevronUp size={14} color="#9CA3AF" />
                  : <IconChevronDown size={14} color="#9CA3AF" />
                }
              </UnstyledButton>

              {driveOffsOpen && (
                <Stack gap={0} pl={16} style={{ borderLeft: '1px solid rgba(255,255,255,0.12)', marginLeft: 20 }}>
                  <UnstyledButton
                    onClick={() => handleNavigate('/manual-entry')}
                    style={{
                      width: '100%',
                      minHeight: 40,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '9px 12px',
                      borderRadius: 8,
                      backgroundColor: path.startsWith('/manual-entry') ? 'rgba(79,70,229,0.25)' : 'transparent',
                    }}
                  >
                    <Text size="14px" fw={path.startsWith('/manual-entry') ? 600 : 400} c={path.startsWith('/manual-entry') ? '#FFFFFF' : '#9CA3AF'}>
                      Bulk Add
                    </Text>
                  </UnstyledButton>
                  <UnstyledButton
                    onClick={() => handleNavigate('/drive-off-report')}
                    style={{
                      width: '100%',
                      minHeight: 40,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '9px 12px',
                      borderRadius: 8,
                      backgroundColor: path.startsWith('/drive-off-report') ? 'rgba(79,70,229,0.25)' : 'transparent',
                    }}
                  >
                    <Text size="14px" fw={path.startsWith('/drive-off-report') ? 600 : 400} c={path.startsWith('/drive-off-report') ? '#FFFFFF' : '#9CA3AF'}>
                      Report
                    </Text>
                  </UnstyledButton>
                </Stack>
              )}

              <UnstyledButton
                onClick={reportsItem.onClick}
                style={{
                  width: '100%',
                  minHeight: 40,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '9px 12px',
                  backgroundColor: reportsItem.active ? '#4F46E5' : 'transparent',
                  color: reportsItem.active ? '#FFFFFF' : '#E5E7EB',
                  border: reportsItem.active ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                  boxShadow: reportsItem.active ? '0 0 0 1px rgba(99,102,241,0.22)' : 'none',
                }}
              >
                <reportsItem.icon size={17} color={reportsItem.active ? '#FFFFFF' : '#9CA3AF'} />
                <Text size="14px" fw={500} c={reportsItem.active ? '#FFFFFF' : '#E5E7EB'}>
                  {reportsItem.label}
                </Text>
              </UnstyledButton>

              <UnstyledButton
                onClick={settingsItem.onClick}
                style={{
                  width: '100%',
                  minHeight: 40,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '9px 12px',
                  backgroundColor: settingsItem.active ? '#4F46E5' : 'transparent',
                  color: settingsItem.active ? '#FFFFFF' : '#E5E7EB',
                  border: settingsItem.active ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                  boxShadow: settingsItem.active ? '0 0 0 1px rgba(99,102,241,0.22)' : 'none',
                }}
              >
                <settingsItem.icon size={17} color={settingsItem.active ? '#FFFFFF' : '#9CA3AF'} />
                <Text size="14px" fw={500} c={settingsItem.active ? '#FFFFFF' : '#E5E7EB'}>
                  {settingsItem.label}
                </Text>
              </UnstyledButton>
            </>
          )}
        </Stack>
      </Stack>

      {effectiveCollapsed ? (
        <Tooltip label="Profile menu" position="right">
          <Box>
            <SidebarProfileMenu compact dark />
          </Box>
        </Tooltip>
      ) : (
        <SidebarProfileMenu dark />
      )}
    </Stack>
  );
};

export default Sidebar;
